/**
 * Capacity protocol shared by every writer of `requestsV03` (API server,
 * Cloud Functions, dashboard).
 *
 * One counter document per restaurant and day. A write that occupies, frees
 * or moves capacity advances the counter of every day it touches IN THE SAME
 * transaction or batch as the reservation write. A writer that must not
 * overbook (the Public API, guest bookings) reads the counter before its
 * availability check and requires it unchanged when it commits; if another
 * writer touched the day in between, it re-checks.
 *
 * Pure data and functions only: this file is copied into the dashboard.
 */

export const CAPACITY_EPOCH_COLLECTION = 'publicReservationCapacityEpochs';

export function capacityEpochId(restaurantId: string, date: string) {
  return `${restaurantId}_${date}`;
}

/**
 * Fields whose change occupies, frees or moves seats or tables. The time
 * blocks are left out on purpose: they are derived from time and length, and
 * back-filling them is not a capacity change.
 */
export const CAPACITY_FIELDS = [
  'date',
  'time',
  'guests',
  'tables',
  'space',
  'status',
  'validTill',
  'reservationLength',
  'occassion',
  'excludeFromSlots',
  'blocked',
  'blockedFullShift',
] as const;

type ReservationLike = Record<string, any> | null | undefined;

function sameValue(a: unknown, b: unknown) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Whether going from `before` to `after` changes capacity; creates and deletes always do. */
export function isCapacityChange(before: ReservationLike, after: ReservationLike) {
  if (!before || !after) return !!(before || after);
  return CAPACITY_FIELDS.some((field) => !sameValue(before[field], after[field]));
}

/**
 * The 15-minute blocks a reservation occupies: its stored time blocks, else
 * derived from time and length. Two reservations on one table conflict when
 * their spans overlap, not only when they start at the same time.
 */
export function occupiedBlocks(reservation: ReservationLike) {
  const [hours, minutes] = String(reservation?.time ?? '00:00').split(':').map(Number);
  const start =
    typeof reservation?.startTimeInBlocks === 'number'
      ? reservation.startTimeInBlocks
      : (hours || 0) * 4 + Math.floor((minutes || 0) / 15);
  const end =
    typeof reservation?.endTimeInBlocks === 'number'
      ? reservation.endTimeInBlocks
      : start + Math.max(1, Number(reservation?.reservationLength ?? 1));
  return { start, end };
}

export function blocksOverlap(a: ReservationLike, b: ReservationLike) {
  const first = occupiedBlocks(a);
  const second = occupiedBlocks(b);
  return first.start < second.end && second.start < first.end;
}

/** Days whose counter a write from `before` to `after` must advance. */
export function capacityDates(before: ReservationLike, after: ReservationLike) {
  return Array.from(
    new Set([before?.date, after?.date].filter((date): date is string => typeof date === 'string' && !!date))
  );
}
